import React, { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router'
import cloneDeep from 'lodash/cloneDeep'

// evertel
import { InfoBox, Button, Input, Modal, ModalBody, Text, ModalHeader, ModalFooter, Label } from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { CreateRoomController } from '@evertel/room'
import { RoomSettingsOptions } from '../aside/RoomSettingsOptions'


interface CreateRoomModalProps {
    visible: boolean
    onClose: () => void
}

const DEFAULT_ROOMCONFIG = {
    isSearchable: false,
    name: '',
    description: '',
    options: {
        autoAddDepartmentMembers: false,
        departmentMembersCanJoinAsMember: true,
        roomMembersCanMessage: true,
        detectScreenshots: true,
        roomMembersCanLeave: true
    }
}

const getDefaultRoomConfig = () => cloneDeep(DEFAULT_ROOMCONFIG)

const CreateRoomModal: React.FC<CreateRoomModalProps> = observer(({
    visible,
    onClose
}) => {

    const createRoomController = useService(CreateRoomController, [])

    const navigate = useNavigate()

    const [error, setError] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [roomConfig, setRoomConfig] = useState(getDefaultRoomConfig())

    const onChange = (e) => {
        setRoomConfig((prev) => ({
            ...prev,
            [e.target.name]: e.target.value
        }))
    }

    const toggleOptions = (option: string, value: boolean) => {
        setRoomConfig(prevConfig => {
            const newConfig = { ...prevConfig }

            if (option === 'isSearchable') {
                newConfig.isSearchable = value
            } else {
                newConfig.options = {
                    ...newConfig.options,
                    [option]: value
                }
            }

            return newConfig
        })
    }

    const createRoom = async () => {
        // no name then ye shall not pass
        if (!roomConfig.name?.trim()) {
            setError('Please give your room a name')
            return
        }

        setIsLoading(true)

        try {
            const newRoom = await createRoomController.createRoom({
                name: roomConfig.name?.trim(),
                description: roomConfig.description?.trim() || '',
                isSearchable: roomConfig.isSearchable,
                options: roomConfig.options
            })

            // close modal and go to new room
            navigate(`/room/${newRoom.id}?new=true`)
            modalClose()

        } catch (error) {
            setError(error?.message || 'Failed to create room')
        } finally {
            setIsLoading(false)
        }
    }

    const modalClose = () => {
        setRoomConfig(getDefaultRoomConfig())

        onClose()
    }

    return (
        <Modal
            visible={visible}
            onClose={modalClose}
            size="lg"
        >
            <ModalHeader
                title="Create a Room"
                subtitle="Name your room and set your room options"
            />
            <ModalBody className="mb-4">
                {(error) &&
                    <InfoBox
                        color="danger"
                        className="text-center"
                    >
                        {error}
                    </InfoBox>
                }
                {/* ROOM NAME */}
                <Label
                    text="Name Your Room"
                    bold
                    htmlFor="name-input"
                />
                <Input
                    id="name-input"
                    name="name"
                    type="text"
                    value={roomConfig.name}
                    onChange={onChange}
                    placeholder="Add a room name..."
                    className="mb-3"
                    maxLength={150}
                />
                {/* ROOM DESCRIPTION */}
                <Label
                    text="Purpose/Description"
                    bold
                    htmlFor="description-input"
                    optional
                />
                <Input
                    id="description-input"
                    name="description"
                    type="text"
                    value={roomConfig.description}
                    onChange={onChange}
                    placeholder="Add a short description..."
                    maxLength={300}
                />
                <RoomSettingsOptions
                    onChangeOption={toggleOptions}
                    options={roomConfig?.options}
                    isSearchable={roomConfig?.isSearchable}
                    hideOptions={['isArchived', 'accessCriteria']}
                    className="mt-5"
                />
            </ModalBody>
            <ModalFooter
                align="right"
            >
                <Button
                    color="primary"
                    className="me-2 my-2"
                    onClick={createRoom}
                    loading={isLoading}
                >
                    Create Room
                </Button>
            </ModalFooter>
        </Modal>
    )
})

export { CreateRoomModal }
